import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { updateRadiologyApi } from "../../services/radiology.service";

/* Wide modal, min height 312 (spec); height auto so toolbar + editor + blue gap + Submit fit */
const DIALOG_SX = {
  "& .MuiDialog-paper": {
    width: "min(1350px, calc(100vw - 32px))",
    maxWidth: "min(1350px, calc(100vw - 32px))",
    height: "auto",
    minHeight: 312,
    maxHeight: "min(92vh, 520px)",
    boxSizing: "border-box",
    borderRadius: 0,
    border: "1px solid #0179B0",
    opacity: 1,
    margin: "16px",
    pt: "35px",
    pr: "50px",
    pb: "35px",
    pl: "50px",
    bgcolor: "#E5F4FF",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  "& .MuiDialogContent-root": {
    padding: 0,
    paddingTop: 0,
    overflow: "hidden",
  },
};

const QUILL_WRAPPER_SX = {
  flex: "0 1 auto",
  display: "flex",
  flexDirection: "column",
  /* Space below white editor — Submit sits on #E5F4FF, not on the editor border */
  mb: "36px",
  "& .ql-toolbar.ql-snow": {
    borderRadius: 0,
    border: "1px solid #ccc",
    borderBottom: "none",
    backgroundColor: "#fff",
    flexShrink: 0,
  },
  "& .ql-container.ql-snow": {
    borderRadius: 0,
    border: "1px solid #ccc",
    flex: "none",
    minHeight: "100px",
    maxHeight: "220px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
  },
  "& .ql-editor": {
    flex: 1,
    minHeight: "72px",
    overflowY: "auto",
  },
};

export default function AddReport({ open, onClose, id, refetch }) {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  const quillModules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ script: "sub" }, { script: "super" }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ header: [1, 2, 3, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        [{ color: [] }, { background: [] }],
        ["blockquote", "code-block"],
        ["clean"],
      ],
      history: {
        delay: 500,
        maxStack: 100,
        userOnly: true,
      },
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "script",
      "size",
      "list",
      "bullet",
      "indent",
      "align",
      "color",
      "background",
      "blockquote",
      "code-block",
    ],
    []
  );

  useEffect(() => {
    if (!open) {
      setValue("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const plain =
      value === "" ||
      value === "<p><br></p>" ||
      value.replace(/<[^>]*>/g, "").trim() === "";
    if (plain) {
      toast.error("Please enter report content.");
      return;
    }

    setLoading(true);
    const response = await updateRadiologyApi(id, {
      status: "Dispatched",
      report: {
        content: value,
      },
    });
    setLoading(false);

    if (response.code === 200 || response.code === 201) {
      toast.success(`Report submitted successfully`);
      refetch?.();
      onClose?.();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      scroll="paper"
      sx={DIALOG_SX}
    >
      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            Add Report
          </Typography>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box sx={QUILL_WRAPPER_SX}>
            <ReactQuill
              theme="snow"
              value={value}
              onChange={setValue}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Write your report here..."
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexShrink: 0,
              width: "100%",
              pt: "4px",
              pb: "4px",
            }}
          >
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
